import React, { useCallback, useEffect, useState } from 'react';
import {
    ArrowDownTrayIcon,
    PaperAirplaneIcon,
    XCircleIcon,
    LockClosedIcon,
    MapPinIcon,
    ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import ActivityDrawer from './ActivityDrawer';
import ActivityStatusBadge from './ActivityStatusBadge';
import ActivityPriorityBadge from './ActivityPriorityBadge';
import PlannedVsActualPanel from './PlannedVsActualPanel';
import ObservationList from './ObservationList';
import FieldMiniMap from './FieldMiniMap';
import {
    fetchAssignment,
    publishAssignment,
    cancelAssignment,
    closeAssignment,
    downloadAssignmentJson,
} from '../../utils/activityApi';
import { canExport } from '../../utils/activityRoles';
import { formatCurrency } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';
import { REQUIREMENT_LABELS, requirementSource, resolveReportingRequirements } from './reportingRequirements';
import type { ActivityReview, AssignmentDetail, FarmField } from './types';

interface AssignmentDetailDrawerProps {
    open: boolean;
    assignmentId: number | null;
    onClose: () => void;
    canManage: boolean;
    fields: FarmField[];
    onReviewReport: (reportId: number) => void;
    onDataChanged: () => void;
}

const detailRowClass = 'flex items-start justify-between gap-4 py-1.5 text-sm';
const labelClass = 'text-xs font-black uppercase tracking-wide text-slate-400';
const valueClass = 'text-right font-semibold text-slate-800';

const formatDateTime = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const formatDate = (value?: string): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const AssignmentDetailDrawer: React.FC<AssignmentDetailDrawerProps> = ({
    open,
    assignmentId,
    onClose,
    canManage,
    fields,
    onReviewReport,
    onDataChanged,
}) => {
    const [detail, setDetail] = useState<AssignmentDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [action, setAction] = useState<'publish' | 'cancel' | 'close' | null>(null);
    const [actionBusy, setActionBusy] = useState(false);
    const [actionError, setActionError] = useState('');
    const [confirming, setConfirming] = useState<'cancel' | 'close' | null>(null);
    const [reason, setReason] = useState('');
    const [exporting, setExporting] = useState(false);

    const { user } = useAuth();
    const allowExport = canExport(user?.role);
    const currencyCode = user?.tenant?.default_currency || 'RWF';

    const load = useCallback(async () => {
        if (!open || !assignmentId) return;
        setLoading(true);
        setError('');
        setActionError('');
        try {
            const data = await fetchAssignment(assignmentId);
            setDetail(data);
        } catch (err: any) {
            setError(err?.message || 'Failed to load assignment.');
        } finally {
            setLoading(false);
        }
    }, [open, assignmentId]);

    useEffect(() => {
        if (open) {
            setConfirming(null);
            setReason('');
            setActionError('');
            setDetail(null);
            load();
        }
    }, [open, load]);

    const assignment = detail?.assignment || null;
    const reports = detail?.reports || [];
    const reviews = detail?.reviews || [];
    const observations = detail?.observations || [];
    const latestReport = reports[0];

    const contract = assignment ? resolveReportingRequirements(assignment) : null;
    const requirementChips = (assignment
        ? [
            'requires_before_photo',
            'requires_photo',
            'requires_after_photo',
            'requires_observations',
            'requires_gps',
            'requires_weather',
            'requires_area_completed',
            'requires_start_time',
            'requires_end_time',
            'requires_materials',
            'requires_equipment',
            'requires_input',
            'requires_signature',
        ]
            .filter((key) => (contract as any)?.[key] === true)
            .map((key) => ({ key, source: requirementSource(assignment, key) }))
        : []) as { key: string; source: 'assignment' | 'type' | 'none' }[];

    const runAction = async (kind: 'publish' | 'cancel' | 'close') => {
        if (!assignment) return;
        setAction(kind);
        setActionBusy(true);
        setActionError('');
        try {
            if (kind === 'publish') {
                await publishAssignment(assignment.id);
            } else if (kind === 'cancel') {
                await cancelAssignment(assignment.id, reason || 'Cancelled from dashboard.');
            } else {
                await closeAssignment(assignment.id);
            }
            setConfirming(null);
            setReason('');
            onDataChanged();
            await load();
        } catch (err: any) {
            const message = err?.message || 'Action failed.';
            if (message.toLowerCase().includes('exceeds the mapped field area') || err?.code === 'AREA_EXCEEDS_FIELD') {
                const proceed = window.confirm(
                    'Planned area exceeds the mapped field area. Publish anyway with a confirmation?',
                );
                if (proceed) {
                    try {
                        await publishAssignment(assignment.id, { override_area_warning: true });
                        setConfirming(null);
                        onDataChanged();
                        await load();
                    } catch (retryErr: any) {
                        setActionError(retryErr?.message || 'Action failed.');
                    }
                }
            } else {
                setActionError(message);
            }
        } finally {
            setActionBusy(false);
            setAction(null);
        }
    };

    const handleExport = async () => {
        if (!assignment) return;
        setExporting(true);
        setActionError('');
        try {
            await downloadAssignmentJson(assignment.id);
        } catch (err: any) {
            setActionError(err?.message || 'Export failed.');
        } finally {
            setExporting(false);
        }
    };

    const field = assignment?.field_plot_id ? fields.find((f) => Number(f.id) === Number(assignment.field_plot_id)) : null;

    const timeline = [
        { label: 'Created', time: assignment?.created_at },
        { label: 'Accepted', time: assignment?.accepted_at },
        { label: 'Started', time: assignment?.started_at },
        { label: 'Submitted', time: assignment?.submitted_at },
        { label: 'Approved', time: assignment?.approved_at },
        { label: 'Closed', time: assignment?.closed_at },
        { label: 'Cancelled', time: assignment?.cancelled_at },
    ].filter((entry) => Boolean(entry.time));

    const planned = assignment
        ? {
              area: assignment.planned_area,
              areaUnit: assignment.planned_area_unit,
              quantity: assignment.planned_quantity,
              quantityUnit: assignment.planned_quantity_unit,
              rate: assignment.planned_rate,
              rateUnit: assignment.planned_rate_unit,
              durationMinutes: assignment.expected_duration_hours ? Number(assignment.expected_duration_hours) * 60 : null,
              material: assignment.material_name,
              materialUnit: assignment.material_unit,
          }
        : null;

    const actual = latestReport
        ? {
              area: latestReport.area_completed,
              areaUnit: latestReport.area_unit,
              quantity: latestReport.quantity_used,
              quantityUnit: latestReport.quantity_unit,
              rate: latestReport.calculated_rate,
              rateUnit: assignment?.planned_rate_unit,
              durationMinutes: latestReport.total_working_minutes,
              material: latestReport.material_name,
              materialUnit: latestReport.material_unit,
          }
        : null;

    return (
        <ActivityDrawer
            open={open}
            onClose={onClose}
            width="xl"
            title={assignment ? assignment.title : 'Assignment'}
            subtitle={
                assignment ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs font-bold text-slate-400">{assignment.assignment_code}</span>
                        <ActivityStatusBadge status={assignment.status} size="sm" />
                        <ActivityPriorityBadge priority={assignment.priority} />
                    </span>
                ) : undefined
            }
        >
            {loading && <div className="py-12 text-center text-sm text-slate-500">Loading assignment…</div>}

            {!loading && error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {!loading && !error && assignment && (
                <div className="space-y-6">
                    {actionError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</div>
                    )}

                    <div className="grid gap-6 lg:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 p-4">
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Identity</p>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Farmer</span>
                                <span className={valueClass}>{assignment.farmer_name || 'Not assigned'}</span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Field</span>
                                <span className={valueClass}>
                                    {assignment.field_name || assignment.plot_code || 'Not mapped'}
                                    {assignment.field_code ? ` (${assignment.field_code})` : ''}
                                </span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Activity</span>
                                <span className={valueClass}>{assignment.activity_type_name || assignment.activity_type || assignment.activity_type_label || '—'}</span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Crop / season</span>
                                <span className={valueClass}>
                                    {[assignment.crop_type, assignment.season_name].filter(Boolean).join(' · ') || '—'}
                                </span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Assigned staff</span>
                                <span className={valueClass}>{assignment.assign_user_name || assignment.assigned_user_name || 'Not assigned'}</span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Published by</span>
                                <span className={valueClass}>{assignment.publisher_name || assignment.published_by_name || '—'}</span>
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 p-4">
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Schedule</p>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Planned start</span>
                                <span className={valueClass}>{formatDate(assignment.planned_start_date)}</span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Due date</span>
                                <span className={valueClass}>{formatDate(assignment.due_date)}</span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Planned area</span>
                                <span className={valueClass}>
                                    {assignment.planned_area ? `${Number(assignment.planned_area).toFixed(2)} ${assignment.planned_area_unit || 'ha'}` : '—'}
                                </span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Material</span>
                                <span className={valueClass}>
                                    {assignment.material_name
                                        ? `${assignment.material_name}${assignment.material_quantity ? ` · ${Number(assignment.material_quantity).toFixed(2)} ${assignment.material_unit || ''}` : ''}`
                                        : '—'}
                                </span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Expected duration</span>
                                <span className={valueClass}>
                                    {assignment.expected_duration_hours ? `${assignment.expected_duration_hours}h` : '—'}
                                </span>
                            </div>
                            <div className={detailRowClass}>
                                <span className={labelClass}>Planned rate</span>
                                <span className={valueClass}>
                                    {assignment.planned_rate ? `${Number(assignment.planned_rate).toFixed(2)} ${assignment.planned_rate_unit || ''}`.trim() : '—'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {Number(assignment.planned_labour_budget) > 0 && (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <p className="text-xs font-black uppercase tracking-[0.16em] text-emerald-800">Planned labour estimate</p>
                                <span className="text-sm font-black text-emerald-900">
                                    {formatCurrency(Number(assignment.planned_labour_budget), currencyCode, { decimals: 2 })}
                                </span>
                            </div>
                            <p className="text-xs leading-5 text-emerald-900/80">
                                {assignment.planned_worker_count ?? '—'} workers · {assignment.planned_workdays ?? '—'} workdays
                                {assignment.planned_hours_per_worker
                                    ? ` · ${assignment.planned_hours_per_worker} hours per worker per day`
                                    : ''}
                            </p>
                            <p className="mt-1 text-[11px] text-emerald-800/70">Plan only; actual attendance and payments are tracked separately.</p>
                        </div>
                    )}

                    {(assignment.work_instructions || assignment.safety_instructions || assignment.equipment_required) && (
                        <div className="grid gap-4 lg:grid-cols-2">
                            {assignment.work_instructions && (
                                <div className="rounded-xl border border-slate-200 p-4">
                                    <p className="mb-1 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Work instructions</p>
                                    <p className="whitespace-pre-wrap text-sm text-slate-700">{assignment.work_instructions}</p>
                                </div>
                            )}
                            {assignment.safety_instructions && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                                    <p className="mb-1 text-xs font-black uppercase tracking-[0.16em] text-amber-600">Safety instructions</p>
                                    <p className="whitespace-pre-wrap text-sm text-amber-900">{assignment.safety_instructions}</p>
                                </div>
                            )}
                            {assignment.equipment_required && (
                                <div className="rounded-xl border border-slate-200 p-4">
                                    <p className="mb-1 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Equipment required</p>
                                    <p className="text-sm text-slate-700">{assignment.equipment_required}</p>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="rounded-xl border border-slate-200 p-4">
                        <div className="mb-2 flex items-center justify-between gap-4">
                            <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">Reporting requirements</p>
                            {contract?.sources?.min_inspection_points === 'assignment' || Number(contract?.min_inspection_points) > 0 ? (
                                <span className="text-xs font-semibold text-slate-600">
                                    {Number(contract?.min_inspection_points) || 0} inspection points
                                </span>
                            ) : null}
                        </div>
                        {requirementChips.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {requirementChips.map((chip) => (
                                    <span
                                        key={chip.key}
                                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                                            chip.source === 'assignment'
                                                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                                : 'border-slate-200 bg-slate-50 text-slate-600'
                                        }`}
                                        title={chip.source === 'assignment' ? 'Pinned on this assignment' : 'Inherited from the activity type'}
                                    >
                                        {REQUIREMENT_LABELS[chip.key] || chip.key}
                                        <span className="text-[10px] font-bold uppercase tracking-wide opacity-70">
                                            {chip.source === 'assignment' ? 'override' : 'from type'}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500">This assignment collects no extra evidence.</p>
                        )}
                    </div>

                    <div>
                        <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Field map</p>
                        <FieldMiniMap
                            field={field}
                            markers={[
                                assignment.start_gps_lat
                                    ? { kind: 'start', label: 'S', lat: assignment.start_gps_lat, lng: assignment.start_gps_lng }
                                    : null,
                                latestReport?.completion_gps_lat
                                    ? { kind: 'end', label: 'E', lat: latestReport.completion_gps_lat, lng: latestReport.completion_gps_lng }
                                    : null,
                            ].filter((m): m is { kind: 'start' | 'end'; label: string; lat: number; lng: number } => Boolean(m))}
                        />
                        {(assignment.start_gps_lat || latestReport?.completion_gps_lat) && (
                            <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                                {assignment.start_gps_lat && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPinIcon className="h-3.5 w-3.5 text-emerald-500" />
                                        Start {Number(assignment.start_gps_lat).toFixed(5)}, {Number(assignment.start_gps_lng).toFixed(5)}
                                    </span>
                                )}
                                {latestReport?.completion_gps_lat && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPinIcon className="h-3.5 w-3.5 text-red-500" />
                                        Completion {Number(latestReport.completion_gps_lat).toFixed(5)}, {Number(latestReport.completion_gps_lng).toFixed(5)}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {planned && actual && (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Planned vs actual</p>
                            <PlannedVsActualPanel
                                planned={planned}
                                actual={actual}
                                variancePercent={latestReport?.variance_percent}
                                varianceExplanation={latestReport?.variance_explanation}
                            />
                        </div>
                    )}

                    {timeline.length > 0 && (
                        <div>
                            <p className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Status timeline</p>
                            <ol className="space-y-0">
                                {timeline.map((entry, index) => (
                                    <li key={entry.label} className="relative flex gap-3 pb-3">
                                        {index < timeline.length - 1 && (
                                            <span className="absolute left-[5px] top-4 h-full w-px bg-slate-200" />
                                        )}
                                        <span
                                            className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full border-2 ${
                                                index === 0 ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white'
                                            }`}
                                        />
                                        <div>
                                            <p className="text-sm font-bold text-slate-700">{entry.label}</p>
                                            <p className="text-xs text-slate-400">{formatDateTime(entry.time)}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {reviews.length > 0 && (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Review history</p>
                            <div className="space-y-2">
                                {reviews.map((review: ActivityReview) => (
                                    <div key={review.id} className="rounded-xl border border-slate-200 p-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-black capitalize text-slate-800">
                                                {String(review.decision).replace(/_/g, ' ')}
                                            </span>
                                            <span className="text-xs text-slate-400">{formatDateTime(review.reviewed_at)}</span>
                                        </div>
                                        {review.correction_instructions && (
                                            <p className="mt-1 text-sm text-orange-700">{review.correction_instructions}</p>
                                        )}
                                        {review.review_comment && <p className="mt-1 text-sm text-slate-600">{review.review_comment}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {reports.length > 0 ? (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Reports ({reports.length})</p>
                            <div className="space-y-2">
                                {reports.map((report) => (
                                    <div key={report.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-mono text-xs font-bold text-slate-500">{report.report_code}</span>
                                                <ActivityStatusBadge status={report.status} size="sm" />
                                            </div>
                                            <p className="mt-1 truncate text-xs text-slate-400">
                                                Submitted {formatDateTime(report.submitted_at)}
                                                {report.worker_count ? ` · ${report.worker_count} workers` : ''}
                                            </p>
                                        </div>
                                        {canManage && ['submitted', 'awaiting_review', 'resubmitted', 'correction_required'].includes(report.status) && (
                                            <button
                                                onClick={() => onReviewReport(report.id)}
                                                className="shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-black text-white hover:bg-slate-700"
                                            >
                                                Review
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-400">
                            <ClipboardDocumentListIcon className="mb-1 inline h-4 w-4" /> No reports submitted yet for this assignment.
                        </div>
                    )}

                    {observations.length > 0 && (
                        <div>
                            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Observations ({observations.length})</p>
                            <ObservationList observations={observations} compact />
                        </div>
                    )}

                    {confirming && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <p className="text-sm font-black text-amber-900">
                                {confirming === 'cancel' ? 'Cancel this assignment?' : 'Close this assignment?'}
                            </p>
                            <p className="mt-1 text-sm text-amber-800">
                                {confirming === 'cancel'
                                    ? 'The assignment will be marked cancelled and removed from the active queue.'
                                    : 'The assignment will be marked closed and archived.'}
                            </p>
                            {confirming === 'cancel' && (
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Reason for cancellation (optional)"
                                    className="mt-3 w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400"
                                    rows={2}
                                />
                            )}
                            <div className="mt-3 flex justify-end gap-2">
                                <button
                                    onClick={() => setConfirming(null)}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-600"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={() => runAction(confirming)}
                                    disabled={actionBusy}
                                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-black text-white hover:bg-red-700 disabled:opacity-60"
                                >
                                    {actionBusy ? 'Working…' : confirming === 'cancel' ? 'Confirm cancel' : 'Confirm close'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
                {canManage && assignment && !confirming && (
                    <>
                        {assignment.status === 'draft' && (
                            <button
                                onClick={() => runAction('publish')}
                                disabled={actionBusy}
                                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                            >
                                <PaperAirplaneIcon className="h-4 w-4" />
                                {action === 'publish' && actionBusy ? 'Publishing…' : 'Publish'}
                            </button>
                        )}
                        {['closed', 'approved', 'rejected', 'cancelled'].includes(assignment.status) === false && (
                            <button
                                onClick={() => setConfirming('cancel')}
                                disabled={actionBusy}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                            >
                                <XCircleIcon className="h-4 w-4" />
                                Cancel
                            </button>
                        )}
                        {!['closed', 'cancelled'].includes(assignment.status) && (
                            <button
                                onClick={() => setConfirming('close')}
                                disabled={actionBusy}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                            >
                                <LockClosedIcon className="h-4 w-4" />
                                Close
                            </button>
                        )}
                    </>
                )}
                {allowExport && assignment && (
                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        {exporting ? 'Exporting…' : 'Export JSON'}
                    </button>
                )}
            </div>
        </ActivityDrawer>
    );
};

export default AssignmentDetailDrawer;
